import React, { ReactNode, useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContextCompat';
import LoadingSpinner from '../ui/LoadingSpinner';
import { supabase } from '../../services/supabase/client';

interface PermissionGuardProps {
  requiredUserType: 'customer' | 'craftsman' | 'company' | 'admin' | 'employee' | 'customer_service' | 'administrative_staff' | 'finance_staff' | 'other_staff' | Array<'customer' | 'craftsman' | 'company' | 'admin' | 'employee' | 'customer_service' | 'administrative_staff' | 'finance_staff' | 'other_staff'>;
  children: ReactNode;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({ 
  requiredUserType, 
  children 
}) => {
  const { currentUser, isLoading } = useAppContext();
  const [waitingForUser, setWaitingForUser] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  
  // 只在開發環境輸出詳細日誌
  const isDev = import.meta.env.DEV;
  if (isDev) {
    console.log('[PermissionGuard]', {
      hasUser: !!currentUser,
      userType: currentUser?.userType,
      requiredType: requiredUserType,
      isLoading,
      waitingForUser,
      hasSession
    });
  }
  
  // 檢查 session 狀態
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const sessionExists = !!data.session;
        setHasSession(sessionExists);
        
        if (sessionExists && !currentUser && !isLoading) {
          if (isDev) {
            console.log('[PermissionGuard] 發現 session 但沒有用戶，等待用戶狀態同步...');
          }
          setWaitingForUser(true);
          
          // 設置一個定時器，如果 5 秒後還沒有用戶，停止等待
          const timeout = setTimeout(() => {
            if (isDev) {
              console.warn('[PermissionGuard] 等待用戶超時，停止等待');
            }
            setWaitingForUser(false);
          }, 5000);
          
          return () => clearTimeout(timeout);
        } else {
          setWaitingForUser(false);
        }
      } catch (error) {
        console.error('[PermissionGuard] 檢查 session 錯誤:', error);
        setHasSession(false);
        setWaitingForUser(false);
      }
    };
    
    checkSession();
  }, [currentUser, isLoading, isDev]);
  
  // 如果用戶出現了，停止等待
  useEffect(() => {
    if (currentUser) {
      if (isDev) {
        console.log('[PermissionGuard] 用戶狀態已同步');
      }
      setWaitingForUser(false);
    }
  }, [currentUser, isDev]);
  
  // 如果正在載入或等待用戶同步，顯示載入畫面
  if (isLoading || waitingForUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner 
          size="lg" 
          text={waitingForUser ? "正在載入用戶資料..." : "正在驗證權限..."}
        />
      </div>
    );
  }
  
  if (!currentUser) {
    if (isDev) {
      console.log('[PermissionGuard] 用戶未登入，重定向到登入頁面');
    }
    return <Navigate to="/auth" replace />;
  }
  
  // 檢查用戶類型權限
  const allowedTypes = Array.isArray(requiredUserType) ? requiredUserType : [requiredUserType];
  const hasPermission = allowedTypes.includes(currentUser.userType as any);
  
  if (isDev) {
    console.log('[PermissionGuard] 權限檢查:', {
      allowedTypes,
      userType: currentUser.userType,
      hasPermission
    });
  }
  
  if (!hasPermission) {
    if (isDev) {
      console.warn('[PermissionGuard] 權限不足，重定向到未授權頁面');
    }
    return <Navigate to="/unauthorized" replace />;
  }
  
  return <>{children}</>;
};
